import React, { useEffect, useRef, useState } from "react";

type Props = {
  itemKey?: string; // X position in the sprite sheet
  width?: number | string;
  height?: number | string;
};

export default function GameItem({
  itemKey = "header",
  width = 100,
  height = "auto",
}: Props) {
  const _scale = 4;

  const itemsImage = "/images/component.png";
  const itemGroups = {
    panda: {
      x: 200 * _scale,
      y: 220 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    header: {
      x: 170 * _scale,
      y: 10 * _scale,
      width: 370 * _scale,
      height: 73 * _scale,
    },
    tokenBalance: {
      x: 10 * _scale,
      y: 70 * _scale,
      width: 110 * _scale,
      height: 40 * _scale,
    },
    coinBalance: {
      x: 10 * _scale,
      y: 110 * _scale,
      width: 110 * _scale,
      height: 40 * _scale,
    },
    banner: {
      x: 540 * _scale,
      y: 10 * _scale,
      width: 360 * _scale,
      height: 170 * _scale,
    },

    // button
    buyTicketButton: {
      x: 10 * _scale,
      y: 10 * _scale,
      width: 160 * _scale,
      height: 60 * _scale,
    },

    // cards
    property: {
      x: 140 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    money_bag: {
      x: 200 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    car: {
      x: 260 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    money: {
      x: 320 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    crown: {
      x: 380 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    gift: {
      x: 440 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    point: {
      x: 10 * _scale,
      y: 220 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    _null: {
      x: 80 * _scale,
      y: 220 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    l3card: {
      x: 80 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
    l4card: {
      x: 10 * _scale,
      y: 150 * _scale,
      width: 60 * _scale,
      height: 60 * _scale,
    },
  };

  const item = itemGroups[itemKey as keyof typeof itemGroups];
  const ref = useRef<HTMLDivElement>(null);
  const [measuredWidth, setMeasuredWidth] = useState<number>(0);

  useEffect(() => {
    if (typeof width === "number") return;

    if (ref.current) {
      setMeasuredWidth(ref.current.offsetWidth);
    }
    const handleResize = () => {
      if (ref.current) {
        setMeasuredWidth(ref.current.offsetWidth);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [width]);

  const scale =
    typeof width === "number"
      ? width / item.width
      : measuredWidth
      ? measuredWidth / item.width
      : 1;

  return (
    <div
      ref={ref}
      style={{
        width: width,
        height: height === "auto" ? item.height * scale : height,
        overflow: "hidden", // ensure clipping works in Safari
        position: "relative",
      }}
    >
      <div
        style={{
          width: item.width,
          height: item.height,
          overflow: "hidden",
          position: "relative",
          transform: `scale(${scale})`,
          transformOrigin: "top left",
          WebkitTransformStyle: "preserve-3d",
          WebkitBackfaceVisibility: "hidden",
          WebkitMaskImage: "radial-gradient(white, black)", // Safari clipping fallback
        }}
      >
        {/* Use background-image cropping to reuse a single decoded image rather than many <img> elements. This reduces memory pressure on low-memory Android devices. */}
        <div
          aria-hidden={false}
          style={{
            position: "absolute",
            left: 0,
            top: 0,
            width: item.width,
            height: item.height,
            display: "block",
            backgroundImage: `url(${itemsImage})`,
            backgroundPosition: `-${item.x}px -${item.y}px`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "auto",
            backfaceVisibility: "hidden",
            WebkitBackfaceVisibility: "hidden",
            maxWidth: "none",
            maxHeight: "none",
            transform: "translate3d(0,0,0)",
            WebkitTransform: "translate3d(0,0,0)",
          }}
        />
      </div>
    </div>
  );
}
